import { Doughnut } from "react-chartjs-2";
import { forwardRef, useImperativeHandle, useRef } from "react";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend
} from "chart.js";
import COLOR_PALETTE from "../../../data/dataChartColors";

ChartJS.register(ArcElement, Tooltip, Legend);

// Eliminadas las interfaces DoughnutChartRef y Props

const getColors = (count) => {
    return Array.from({ length: count }, (_, i) => COLOR_PALETTE[i % COLOR_PALETTE.length]);
};

const valueLabelsPlugin = {
    id: "valueLabelsPlugin",
    afterDatasetsDraw(chart) {
        const { ctx } = chart;

        chart.data.datasets.forEach((dataset, i) => {
            const meta = chart.getDatasetMeta(i);

            meta.data.forEach((element, index) => {
                const value = dataset.data[index];
                const pos = element.tooltipPosition();

                ctx.save();
                ctx.font = "bold 12px sans-serif";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";

                ctx.lineWidth = 3;
                ctx.strokeStyle = "white";
                ctx.strokeText(value, pos.x, pos.y);

                ctx.fillStyle = "#000";
                ctx.fillText(value, pos.x, pos.y);

                ctx.restore();
            });
        });
    }
};

const centerTextPlugin = {
    id: "centerTextPlugin",
    afterDatasetsDraw(chart) {
        const { ctx } = chart;

        if (!chart.chartArea) return;

        const { left, top, width, height } = chart.chartArea;
        const x = left + width / 2;
        const y = top + height / 2;

        const total = chart.data.datasets[0].data.reduce((s, v) => s + v, 0);

        ctx.save();
        ctx.textAlign = "center";

        ctx.font = "16px sans-serif";
        ctx.fillStyle = "#000";
        ctx.textBaseline = "bottom";
        ctx.fillText(String(total), x, y);

        ctx.font = "12px sans-serif";
        ctx.fillStyle = "#666";
        ctx.textBaseline = "top";
        ctx.fillText("Total", x, y + 5);

        ctx.restore();
    }
};

// Eliminados los genéricos <DoughnutChartRef, Props>
const DoughnutChart = forwardRef(({ data }, ref) => {
    const canvasRef = useRef(null);
    const chartInstanceRef = useRef(null);

    useImperativeHandle(ref, () => ({
        getCanvas: () => canvasRef.current,
        toggleLegend: (show) => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.options.plugins.legend.display = show;
                chartInstanceRef.current.update();
            }
        },
        getLegendData: () => {
            const colors = getColors(data.length);
            return data.map((d, i) => ({
                label: d.name,
                value: d.quantity.toString(),
                color: colors[i]
            }));
        }
    }));

    const chartData = {
        labels: data.map((d) => d.name),
        datasets: [
            {
                label: "Cantidad",
                data: data.map((d) => d.quantity),
                backgroundColor: getColors(data.length),
                borderWidth: 1
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
            legend: {
                display: false, 
                position: 'bottom', // Eliminado "as const"
                align: 'start',      // Eliminado "as const"
                labels: {
                    boxWidth: 12,
                    padding: 8,
                    font: {
                        size: 9
                    },
                    generateLabels: (chart) => {
                        const data = chart.data;
                        if (data.labels.length && data.datasets.length) {
                            return data.labels.map((label, i) => {
                                const value = data.datasets[0].data[i];
                                return {
                                    text: `${label}: ${value}`,
                                    fillStyle: data.datasets[0].backgroundColor[i],
                                    hidden: false,
                                    index: i
                                };
                            });
                        }
                        return [];
                    }
                }
            },
            valueOnTop: false
        },
        layout: {
            padding: {
                bottom: 10 
            }
        }
    };

    return (
        <Doughnut
            data={chartData}
            options={options}
            plugins={[valueLabelsPlugin, centerTextPlugin]}
            ref={(instance) => {
                if (instance && instance.canvas) {
                    canvasRef.current = instance.canvas;
                    chartInstanceRef.current = instance;
                }
            }}
        />
    );
});

export default DoughnutChart;